import {
  connectorIntentEnablement,
  type ConnectorManifest,
  type FolderKind,
  type ImportTarget,
} from '../connectorRegistry';
import type { SourceMappingProfile } from '../../../domains/knowledge-ingestion/models';
import type {
  FetchQuery,
  FetchResult,
} from '../../../domains/data-binding/services/adapters/IDataSourceAdapter';
import { MAX_SOURCE_IMPORT_ROWS } from '../../../domains/data-binding/models/SourceImportLimits';
import type { SourceBindingReviewVersion } from '../../../domains/data-binding/models/SourceSchemaRecovery';

export interface TabularImportRecord {
  readonly id: string;
  readonly data: Record<string, unknown>;
}

/**
 * Hard ceiling on rows a single tabular import will pull client-side.
 * Anything beyond it must be surfaced to the user as truncation — never
 * dropped silently.
 */
export const MAX_TABULAR_IMPORT_RECORDS = MAX_SOURCE_IMPORT_ROWS;

export function tabularImportLimitError(input: {
  readonly loaded: number;
  readonly total: number;
  readonly hasMore: boolean;
}): string | null {
  if (!input.hasMore && input.total <= input.loaded) return null;
  const observedTotal = input.total > input.loaded
    ? input.total.toLocaleString()
    : `more than ${input.loaded.toLocaleString()}`;
  return `This source contains ${observedTotal} rows. Imports support up to ${MAX_TABULAR_IMPORT_RECORDS.toLocaleString()} rows at a time. No rows were imported.`;
}

export interface FetchAllAdapterRecordsResult {
  readonly records: TabularImportRecord[];
  readonly total: number;
  readonly truncated: boolean;
}

export interface TabularRecordsAdapter {
  readonly capabilities?: { readonly maxRecordsPerRequest?: number };
  fetchRecords(query: FetchQuery): Promise<FetchResult>;
}

interface ConnectedSourceRecordsResponse {
  readonly records?: TabularImportRecord[];
  readonly total?: number;
  readonly hasMore?: boolean;
  readonly error?: string;
}

/**
 * Drains an adapter table by paging `fetchRecords` until `hasMore` is
 * false or `maxRecords` is reached. Adapters default `limit` when it is
 * omitted (CsvUploadAdapter uses 100), so a single unpaged call silently
 * truncates larger files — always import through this helper.
 */
export async function fetchAllAdapterRecords(
  adapter: TabularRecordsAdapter,
  tableId: string,
  maxRecords: number = MAX_TABULAR_IMPORT_RECORDS,
): Promise<FetchAllAdapterRecordsResult> {
  const pageSize = Math.max(
    1,
    Math.min(adapter.capabilities?.maxRecordsPerRequest ?? 1000, maxRecords),
  );
  const records: TabularImportRecord[] = [];
  let offset = 0;
  let total = 0;
  let hasMore = true;

  while (hasMore && records.length < maxRecords) {
    const page = await adapter.fetchRecords({
      tableId,
      offset,
      limit: Math.min(pageSize, maxRecords - records.length),
    });
    const pageRecords = page.records ?? [];
    records.push(...pageRecords);
    total = Math.max(page.total ?? 0, records.length);
    if (pageRecords.length === 0) break;
    hasMore = Boolean(page.hasMore);
    offset += pageRecords.length;
  }

  return {
    records,
    total,
    truncated:
      hasMore && (records.length >= maxRecords || total > records.length),
  };
}

/**
 * Drains the authenticated connected-source endpoint in bounded pages. A
 * connected provider may impose a smaller page size than the product import
 * cap, so one `limit=10000` request is not evidence that the snapshot is
 * complete.
 */
export async function fetchAllConnectedSourceRecords(
  fetcher: TabularImportApplyInput['fetcher'],
  dataSourceId: string,
  maxRecords: number = MAX_TABULAR_IMPORT_RECORDS,
  pageSize: number = 1_000,
): Promise<FetchAllAdapterRecordsResult> {
  const boundedPageSize = Math.max(1, Math.min(pageSize, maxRecords));
  const records: TabularImportRecord[] = [];
  let offset = 0;
  let total = 0;
  let hasMore = true;

  while (hasMore && records.length < maxRecords) {
    const limit = Math.min(boundedPageSize, maxRecords - records.length);
    const response = await fetcher(
      `/api/data-sources/${encodeURIComponent(dataSourceId)}/records?limit=${limit}&offset=${offset}`,
      { cache: 'no-store' },
    );
    const body = (await response.json().catch(() => ({}))) as ConnectedSourceRecordsResponse;
    if (!response.ok) {
      throw new Error(body.error ?? `record fetch failed with status ${response.status}`);
    }
    const pageRecords = body.records ?? [];
    const remaining = maxRecords - records.length;
    records.push(...pageRecords.slice(0, remaining));
    total = Math.max(body.total ?? 0, records.length);
    hasMore = Boolean(body.hasMore)
      || total > records.length
      || pageRecords.length > remaining;
    if (pageRecords.length === 0) break;
    offset += pageRecords.length;
  }

  return {
    records,
    total,
    truncated: hasMore || total > records.length,
  };
}

export interface TabularImportBatch {
  readonly sourceLabel: string;
  readonly records: readonly TabularImportRecord[];
}

export interface TabularImportApplyInput {
  readonly reviewedBinding?: SourceBindingReviewVersion;
  readonly syncMode?: 'manual' | 'scheduled';
  readonly fetcher: (input: string, init?: RequestInit) => Promise<Response>;
  readonly folderId: string;
  readonly manifest: ConnectorManifest;
  readonly dataSourceId?: string | null;
  readonly sourceTableId?: string | null;
  readonly selectedIntent?: string | null;
  readonly profileId: string;
  readonly target: ImportTarget;
  readonly batch: TabularImportBatch;
  readonly mappingProfile?: SourceMappingProfile;
  /**
   * Server-side handle for the records already uploaded at preview time.
   * When set, apply sends `{ previewUploadId }` instead of re-uploading the
   * full records array. Preview-backed imports fail closed when that durable
   * payload is unavailable; they must never fall back to the synchronous
   * inline path and bypass queue-backed staging.
   */
  readonly previewUploadId?: string | null;
  /** Confirms that this preview's mapping-contract changes were explicitly reviewed. */
  readonly acceptReviewedSchemaChange?: boolean;
  /** Test/host overrides; production defaults poll every 2s for 30 minutes. */
  readonly runPollIntervalMs?: number;
  readonly runPollTimeoutMs?: number;
}

export interface TabularImportApplyResult {
  readonly createdCount: number;
  readonly updatedCount: number;
  readonly unresolvedCount?: number;
  readonly bindingId?: string;
  readonly runId?: string;
  readonly completionState?: string;
  readonly reconciliationMode?: 'exact' | 'assisted';
  readonly automaticRemovalDetection?: boolean;
}

export interface TabularImportPreviewProfile {
  readonly profileId: string;
  readonly created: number;
  readonly changed: number;
  readonly unchanged: number;
  readonly conflicts: number;
  readonly changedFields: readonly string[];
}

export interface TabularImportPreviewIssue {
  readonly reason: 'missing_required_field' | 'missing_entity' | 'ambiguous_reference';
  readonly kind: 'entity' | 'relation';
  readonly count: number;
  readonly missingFields: readonly string[];
  readonly missingEntityRefs: readonly string[];
}

export interface TabularImportPreviewResult {
  readonly summary: {
    readonly sourceRows: number;
    readonly materializedRecords: number;
    readonly created: number;
    readonly changed: number;
    readonly unchanged: number;
    readonly unresolved: number;
    readonly conflicts: number;
    readonly protectedFieldCount: number;
  };
  readonly profiles: readonly TabularImportPreviewProfile[];
  readonly issues: readonly TabularImportPreviewIssue[];
  readonly preflight: {
    readonly reviewBlocking: boolean;
    readonly issueCount: number;
    readonly reasonCodes: readonly string[];
    readonly containsProtectedValues: false;
  } | null;
  readonly reviewBlocking: boolean;
  readonly containsProtectedValues: false;
  /** Handle for the uploaded records — pass to apply to skip re-uploading. */
  readonly previewUploadId?: string;
}

export interface OwnerImportFullAccessResult {
  readonly created: boolean;
  readonly protectedFieldCount: number;
}

/**
 * Apply failure with the human-readable summary as `message` and the
 * verbatim server payload (query dumps, serialized commit JSON) tucked
 * into `detail` for an optional collapsed "technical details" view —
 * never rendered inline.
 */
export class TabularImportApplyError extends Error {
  readonly detail?: string;

  constructor(message: string, detail?: string) {
    super(message);
    this.name = 'TabularImportApplyError';
    if (detail) this.detail = detail;
  }
}

const READABLE_ERROR_MAX_LENGTH = 300;

function looksLikeSerializedPayload(value: string): boolean {
  return (
    value.startsWith('{') ||
    value.startsWith('[') ||
    value.startsWith('Failed query:') ||
    value.includes('"decisionId"')
  );
}

function importApplyError(
  rawError: unknown,
  status: number,
  result?: { fetched?: number; failed?: number },
): TabularImportApplyError {
  const raw = typeof rawError === 'string' ? rawError.trim() : '';
  const firstLine = raw.split('\n', 1)[0].trim();
  const readable =
    firstLine.length > 0 &&
    firstLine.length <= READABLE_ERROR_MAX_LENGTH &&
    !looksLikeSerializedPayload(firstLine);
  const failedCount = result?.failed ?? result?.fetched ?? 0;
  const fallback =
    failedCount > 0
      ? `Import failed — ${failedCount.toLocaleString()} record${failedCount === 1 ? '' : 's'} could not be committed (status ${status}).`
      : `Import failed (status ${status}).`;
  const detail = readable
    ? raw !== firstLine
      ? raw
      : undefined
    : raw || undefined;
  return new TabularImportApplyError(readable ? firstLine : fallback, detail);
}

const DEFAULT_PROFILE_BY_FOLDER_KIND: Partial<Record<FolderKind, string>> = {
  orgchart: 'orgchart',
  process: 'process',
  network: 'network',
  genogram: 'genogram',
  grc: 'grc-control',
  strategy: 'gantt',
  software_design: 'component',
  workspace: 'workspace',
  compass: 'compass',
};

export function headersFromRecords(records: readonly TabularImportRecord[]): string[] {
  const headers = new Set<string>();
  for (const record of records.slice(0, 50)) {
    Object.keys(record.data ?? {}).forEach((key) => headers.add(key));
  }
  return Array.from(headers);
}

export function rawRecordInputs(
  records: readonly TabularImportRecord[],
): Array<{ externalId: string | null; universalProps: Record<string, unknown> }> {
  return records.map((record) => ({
    externalId: record.id ?? null,
    universalProps: record.data ?? {},
  }));
}

export function preferredProfileId(
  manifest: ConnectorManifest,
  folderKind: FolderKind,
): string {
  const preferred = DEFAULT_PROFILE_BY_FOLDER_KIND[folderKind];
  if (preferred && manifest.compatibleProfileIds.includes(preferred)) return preferred;
  return manifest.compatibleProfileIds[0] ?? folderKind;
}

export interface EncodedJsonRequestBody {
  readonly body: BodyInit;
  readonly headers: Record<string, string>;
}

/**
 * Serializes a JSON payload for upload, gzipping it when the browser supports
 * `CompressionStream`. Browsers never compress fetch bodies on their own, so
 * the multi-megabyte import record arrays crawl on low-upstream links (a
 * measured 202s preview upload on a ~60kbps uplink). The custom
 * `x-body-encoding: gzip` header — not the standard `content-encoding`
 * request header, which intermediaries may strip or reject — tells the
 * server to gunzip before parsing.
 */
export async function encodeJsonRequestBody(
  payload: unknown,
): Promise<EncodedJsonRequestBody> {
  const json = JSON.stringify(payload);
  if (typeof CompressionStream === 'undefined') {
    return { body: json, headers: { 'Content-Type': 'application/json' } };
  }
  const compressed = new Blob([json])
    .stream()
    .pipeThrough(new CompressionStream('gzip'));
  return {
    body: await new Response(compressed).arrayBuffer(),
    headers: {
      'Content-Type': 'application/json',
      'x-body-encoding': 'gzip',
    },
  };
}

async function postJsonEncoded(
  fetcher: TabularImportApplyInput['fetcher'],
  url: string,
  payload: unknown,
): Promise<Response> {
  const encoded = await encodeJsonRequestBody(payload);
  return fetcher(url, {
    method: 'POST',
    credentials: 'include',
    headers: encoded.headers,
    body: encoded.body,
  });
}

export async function applyTabularImport(
  input: TabularImportApplyInput,
): Promise<TabularImportApplyResult> {
  if (input.target !== 'folder') {
    throw new Error(
      'diagram and both tabular targets require a host CommandManager materializer — use target=folder until the host wires command-backed canvas import',
    );
  }

  const selectedIntent =
    input.selectedIntent ??
    input.mappingProfile?.intent ??
    input.manifest.source?.defaultIntent ??
    null;
  if (
    selectedIntent &&
    input.manifest.source?.supportedIntents &&
    connectorIntentEnablement(input.manifest, selectedIntent) !== 'enabled'
  ) {
    throw new Error(
      `${input.manifest.label} is not enabled for the ${selectedIntent} purpose`,
    );
  }

  const postBody = tabularImportPostBody(input, selectedIntent);
  const applyUrl = `/api/folders/${encodeURIComponent(input.folderId)}/source-bindings`;

  let resp: Response;
  if (input.previewUploadId) {
    const { records: _records, ...withoutRecords } = postBody;
    resp = await postJsonEncoded(input.fetcher, applyUrl, {
      ...withoutRecords,
      previewUploadId: input.previewUploadId,
    });
  } else {
    resp = await postJsonEncoded(input.fetcher, applyUrl, postBody);
  }
  if (!resp.ok) {
    const body = (await resp.json().catch(() => ({}))) as {
      error?: string;
      result?: { fetched?: number; failed?: number };
    };
    throw importApplyError(body.error, resp.status, body.result);
  }

  let body = (await resp.json()) as {
    binding?: { id?: string };
    run?: {
      id?: string;
      status?: string;
      error?: string | null;
      completionState?: string;
      completionEvidence?: {
        identityMode?: unknown;
        absenceInferenceEligible?: unknown;
      };
    };
    result?: { created?: number; updated?: number; unresolved?: number };
  };
  if (resp.status === 202) {
    const bindingId = body.binding?.id;
    const runId = body.run?.id;
    if (!bindingId || !runId) {
      throw new TabularImportApplyError('Import was queued without a run identifier.');
    }
    body = await pollSourceImportRun(input, bindingId, runId);
  }
  const identityMode = body.run?.completionEvidence?.identityMode;
  return {
    createdCount: body.result?.created ?? 0,
    updatedCount: body.result?.updated ?? 0,
    ...(typeof body.result?.unresolved === 'number'
      ? { unresolvedCount: body.result.unresolved }
      : {}),
    ...(body.binding?.id ? { bindingId: body.binding.id } : {}),
    ...(body.run?.id ? { runId: body.run.id } : {}),
    ...(body.run?.completionState
      ? { completionState: body.run.completionState }
      : {}),
    ...(identityMode === 'exact' || identityMode === 'assisted'
      ? { reconciliationMode: identityMode }
      : {}),
    ...(typeof body.run?.completionEvidence?.absenceInferenceEligible === 'boolean'
      ? {
          automaticRemovalDetection:
            body.run.completionEvidence.absenceInferenceEligible,
        }
      : {}),
  };
}

async function pollSourceImportRun(
  input: TabularImportApplyInput,
  bindingId: string,
  runId: string,
): Promise<{
  binding: { id: string };
  run: {
    id: string;
    status?: string;
    error?: string | null;
    completionState?: string;
    completionEvidence?: {
      identityMode?: unknown;
      absenceInferenceEligible?: unknown;
    };
  };
  result: { created?: number; updated?: number; unresolved?: number };
}> {
  const startedAt = Date.now();
  const timeoutMs = input.runPollTimeoutMs ?? 30 * 60 * 1_000;
  const intervalMs = input.runPollIntervalMs ?? 2_000;
  const url = `/api/folders/${encodeURIComponent(input.folderId)}/source-bindings/${encodeURIComponent(bindingId)}/runs?limit=20`;
  while (Date.now() - startedAt <= timeoutMs) {
    const response = await input.fetcher(url, { credentials: 'include' });
    const payload = (await response.json().catch(() => ({}))) as {
      error?: string;
      runs?: Array<{
        id?: string;
        status?: string;
        error?: string | null;
        createdCount?: number;
        updatedCount?: number;
        unresolvedCount?: number;
        completionState?: string;
        completionEvidence?: {
          identityMode?: unknown;
          absenceInferenceEligible?: unknown;
        };
      }>;
    };
    if (!response.ok) throw importApplyError(payload.error, response.status);
    const run = payload.runs?.find((candidate) => candidate.id === runId);
    if (run?.status === 'failed') {
      throw importApplyError(run.error, 500, { failed: 0 });
    }
    if (run?.status === 'succeeded') {
      return {
        binding: { id: bindingId },
        run: {
          id: runId,
          status: run.status,
          error: run.error,
          completionState: run.completionState,
          completionEvidence: run.completionEvidence,
        },
        result: {
          created: run.createdCount,
          updated: run.updatedCount,
          unresolved: run.unresolvedCount,
        },
      };
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new TabularImportApplyError('Import is still running. Check Sources for its status.');
}

/**
 * Runs canonical materialization, protected-field classification, bounded
 * repository comparison, and structural preflight without creating a source
 * binding or import run and without writing folder records.
 */
export async function previewTabularImport(
  input: TabularImportApplyInput,
): Promise<TabularImportPreviewResult> {
  if (input.target !== 'folder') {
    throw new Error('Import preview is available only for folder-owned sources');
  }
  const selectedIntent =
    input.selectedIntent ??
    input.mappingProfile?.intent ??
    input.manifest.source?.defaultIntent ??
    null;
  if (
    selectedIntent &&
    input.manifest.source?.supportedIntents &&
    connectorIntentEnablement(input.manifest, selectedIntent) !== 'enabled'
  ) {
    throw new Error(
      `${input.manifest.label} is not enabled for the ${selectedIntent} purpose`,
    );
  }

  const response = await postJsonEncoded(
    input.fetcher,
    `/api/folders/${encodeURIComponent(input.folderId)}/source-bindings/preview`,
    tabularImportPostBody(input, selectedIntent),
  );
  const body = (await response.json().catch(() => ({}))) as {
    error?: string;
    preview?: TabularImportPreviewResult;
    previewUploadId?: string;
  };
  if (!response.ok || !body.preview) {
    throw importApplyError(
      body.error ?? 'Import preview failed',
      response.status,
    );
  }
  if (typeof body.previewUploadId !== 'string' || body.previewUploadId.length === 0) {
    throw new TabularImportApplyError(
      'Import preview could not be saved for background processing. Review the import again.',
    );
  }
  return { ...body.preview, previewUploadId: body.previewUploadId };
}

function tabularImportPostBody(
  input: TabularImportApplyInput,
  selectedIntent: string | null,
): Record<string, unknown> {
  return {
    ...(input.reviewedBinding ? { reviewedBinding: input.reviewedBinding } : {}),
    ...(input.syncMode ? { syncMode: input.syncMode } : {}),
    connectorId: input.manifest.id,
    dataSourceId: input.dataSourceId ?? null,
    sourceTableId: input.sourceTableId ?? null,
    sourceLabel: input.batch.sourceLabel,
    selectedIntent,
    targetProfileId: input.mappingProfile?.targetProfileId ?? input.profileId,
    mappingProfile: input.mappingProfile ?? null,
    records: input.batch.records,
    acceptReviewedSchemaChange: input.acceptReviewedSchemaChange === true,
  };
}

/**
 * Asks the server to suggest roles for columns the deterministic matcher could
 * not place. Suggestions only pre-fill the mapping review — the user still
 * confirms, and any failure leaves the deterministic mapping untouched, so this
 * never blocks or fails an import.
 */
export async function fetchAssistedHeaderRoles(input: {
  readonly fetcher: (input: string, init?: RequestInit) => Promise<Response>;
  readonly folderId: string;
  readonly headers: readonly string[];
  readonly sampleRows?: readonly Record<string, unknown>[];
  readonly sourceSystem?: string;
  readonly profileId?: string;
}): Promise<Record<string, string>> {
  if (input.headers.length === 0) return {};
  try {
    const response = await input.fetcher(
      `/api/folders/${encodeURIComponent(input.folderId)}/source-bindings/header-roles`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          headers: input.headers,
          sampleRows: input.sampleRows?.slice(0, 10) ?? [],
          ...(input.sourceSystem ? { sourceSystem: input.sourceSystem } : {}),
          ...(input.profileId ? { profileId: input.profileId } : {}),
        }),
      },
    );
    if (!response.ok) return {};
    const body = (await response.json().catch(() => ({}))) as {
      assistedSelections?: Record<string, unknown>;
    };
    const out: Record<string, string> = {};
    for (const [roleId, header] of Object.entries(body.assistedSelections ?? {})) {
      if (typeof header === 'string' && input.headers.includes(header)) {
        out[roleId] = header;
      }
    }
    return out;
  } catch {
    return {};
  }
}

export async function grantOwnerImportFullAccess(input: {
  readonly fetcher: (input: string, init?: RequestInit) => Promise<Response>;
  readonly folderId: string;
  readonly bindingId: string;
}): Promise<OwnerImportFullAccessResult> {
  const response = await input.fetcher(
    `/api/folders/${encodeURIComponent(input.folderId)}/data-grants/imports/${encodeURIComponent(input.bindingId)}/owner-full-access`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  const body = (await response.json().catch(() => ({}))) as {
    created?: boolean;
    protectedFieldCount?: number;
    error?: string;
  };
  if (!response.ok) {
    throw new Error(body.error ?? 'Full Atlas access could not be granted');
  }
  return {
    created: body.created === true,
    protectedFieldCount:
      typeof body.protectedFieldCount === 'number'
        ? body.protectedFieldCount
        : 0,
  };
}
